import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndProvider, useDragDropManager } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  Plate,
  PlateContent,
  ParagraphPlugin,
  createPlatePlugin,
  useEditorRef,
  usePlateEditor,
} from "platejs/react";
import type { Path, TElement } from "platejs";
import { NodeIdPlugin, PathApi } from "platejs";
import { DndPlugin, useDraggable, useDropLine } from "@platejs/dnd";

import type { AssemblyProject, SelectableItem } from "../../../common/contexts/SelectionContext";
import { useSelection } from "../../../common/contexts/SelectionContext";
import { useAuth } from "../../../auth/contexts/AuthContext";
import { useTags } from "../../../common/contexts/TagsContext";
import { useProjects } from "../../../common/contexts/ProjectContext";
import { BlockRowContext } from "../LogCard";

// `project.createdBy` viene del backend como UUID del autor (no como nombre).
// Para mostrar/exportar el nombre real lo cruzamos contra `participantList`.
// Si no encontramos match (autor que ya no es participante, datos aún sin
// cargar, o `createdBy` no es un UUID sino que ya es un nombre legible)
// caemos al user actual y luego a "—". Nunca exponemos el UUID crudo.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function resolveAutorName(
  createdBy: string | null | undefined,
  participantList: any[],
  fallbackName: string | null | undefined,
): string {
  if (createdBy) {
    const p = participantList.find((pp: any) => pp?.id === createdBy);
    const name = p?.name || p?.alias;
    if (name) return name;
    if (!UUID_RE.test(createdBy)) return createdBy;
  }
  return fallbackName || "—";
}
import { docToItems, itemToBlock, seedFromProject } from "./seed";
import { buildExportTxt, downloadTxt, sanitizeFilename } from "./exportTxt";
import { buildExportPdf, downloadPdf } from "./exportPdf";
import { buildExportDoc, downloadDoc } from "./exportDoc";
import { toast } from "sonner";
import {
  BLOCK_CHECKLIST,
  BLOCK_LOG,
  BLOCK_NOTA,
  BLOCK_OTF,
  BLOCK_PLECA,
  BLOCK_TRANSCRIPT,
  CortesEditorContext,
} from "./schema";
import { ParagraphBlock } from "./blocks/ParagraphBlock";
import { LogBlock } from "./blocks/LogBlock";
import { OtfBlock } from "./blocks/OtfBlock";
import { TranscriptBlock } from "./blocks/TranscriptBlock";
import { ChecklistBlock } from "./blocks/ChecklistBlock";
import { PlecaBlock } from "./blocks/PlecaBlock";
import { NotaBlock } from "./blocks/NotaBlock";

const HeadingPlugin = createPlatePlugin({
  key: "h2",
  node: {
    isElement: true,
    component: ({ children, attributes }: any) => (
      <h2
        {...attributes}
        style={{
          fontSize: "1rem",
          fontWeight: 800,
          color: "#fff",
          margin: "12px 0 8px",
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        {children}
      </h2>
    ),
  },
});

const LogPlugin = createPlatePlugin({
  key: BLOCK_LOG,
  node: { isElement: true, component: LogBlock as any },
});

const OtfPlugin = createPlatePlugin({
  key: BLOCK_OTF,
  node: { isElement: true, component: OtfBlock as any },
});

const TranscriptPlugin = createPlatePlugin({
  key: BLOCK_TRANSCRIPT,
  node: { isElement: true, component: TranscriptBlock as any },
});

const ChecklistPlugin = createPlatePlugin({
  key: BLOCK_CHECKLIST,
  node: { isElement: true, isVoid: true, component: ChecklistBlock as any },
});

// Exportar a PDF está oculto temporalmente en la UI. La lógica
// (handleExportPdf / buildExportPdf) se conserva: cambiar a `true` lo reactiva.
const SHOW_PDF_EXPORT = false;

const PlecaPlugin = createPlatePlugin({
  key: BLOCK_PLECA,
  node: { isElement: true, component: PlecaBlock as any },
});

const NotaPlugin = createPlatePlugin({
  key: BLOCK_NOTA,
  node: { isElement: true, component: NotaBlock as any },
});

function BlockRow({ element, children }: { element: TElement; children: React.ReactNode }) {
  // `useDraggable` wires BOTH the drag source (handleRef) AND the drop target
  // (nodeRef — it's measured for hover direction). Connecting only the handle,
  // as before, left nodeRef.current null so the drop target never registered
  // and reordering silently did nothing. nodeRef must sit on a real block DOM
  // node; the row container works for hit-testing.
  const editor = useEditorRef();
  const editorCtx = React.useContext(CortesEditorContext);
  const { nodeRef, handleRef, isDragging } = useDraggable({ element });
  // Plate tracks the hovered drop position but doesn't render it — draw the
  // landing indicator ourselves from the computed direction.
  const { dropLine } = useDropLine({ id: (element as any).id });

  const removeBlock = () => {
    const path = editor.api.findPath(element);
    if (path) editor.tf.removeNodes({ at: path });
  };

  const isCardBlock = [BLOCK_LOG, BLOCK_TRANSCRIPT, BLOCK_OTF, BLOCK_PLECA, BLOCK_NOTA].includes(
    element.type as string,
  );

  return (
    <div
      ref={nodeRef}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: isCardBlock ? 0 : 4,
        position: "relative",
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {(dropLine === "top" || dropLine === "bottom") && (
        <div
          contentEditable={false}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            // Center the indicator on the row's edge so it reads as a single
            // line sitting in the gap between cards, not hugging either one.
            [dropLine === "top" ? "top" : "bottom"]: 0,
            transform: dropLine === "top" ? "translateY(-50%)" : "translateY(50%)",
            height: 2,
            borderRadius: 2,
            background: "var(--color-role-sp, #22c55e)",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />
      )}
      {!isCardBlock && (
        <div
          contentEditable={false}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            paddingTop: 4,
          }}
        >
          <span
            ref={handleRef as any}
            title="Arrastrar"
            style={{
              cursor: "grab",
              userSelect: "none",
              color: "#555",
              fontSize: "0.9rem",
              padding: "0 4px",
              opacity: isDragging ? 0.4 : 0.7,
            }}
          >
            ⋮⋮
          </span>
          <button
            type="button"
            title="Eliminar bloque"
            onMouseDown={(e) => e.preventDefault()}
            onClick={removeBlock}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#555",
              fontSize: "0.85rem",
              padding: "0 4px",
              lineHeight: 1,
            }}
          >
            <i className="bx bx-trash" />
          </button>
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isCardBlock ? (
          <BlockRowContext.Provider
            value={{
              handleRef,
              onDeleteBlock: removeBlock,
              onNavigateSource: editorCtx?.onNavigateSource,
            }}
          >
            {children}
          </BlockRowContext.Provider>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function ParagraphWithHandle(props: any) {
  return (
    <BlockRow element={props.element}>
      <ParagraphBlock {...props} />
    </BlockRow>
  );
}

function HeadingWithHandle(props: any) {
  return (
    <BlockRow element={props.element}>
      <h2
        {...props.attributes}
        style={{
          fontSize: "1rem",
          fontWeight: 800,
          color: "#fff",
          margin: "12px 0 8px",
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        {props.children}
      </h2>
    </BlockRow>
  );
}

function wrapWithHandle<P extends { element: TElement }>(Comp: React.ComponentType<P>) {
  return function Wrapped(props: P) {
    return (
      <BlockRow element={props.element}>
        <Comp {...props} />
      </BlockRow>
    );
  };
}

const LogPluginWithHandle = LogPlugin.withComponent(wrapWithHandle(LogBlock as any) as any);
const OtfPluginWithHandle = OtfPlugin.withComponent(wrapWithHandle(OtfBlock as any) as any);
const TranscriptPluginWithHandle = TranscriptPlugin.withComponent(
  wrapWithHandle(TranscriptBlock as any) as any,
);
const ChecklistPluginWithHandle = ChecklistPlugin.withComponent(
  wrapWithHandle(ChecklistBlock as any) as any,
);
const HeadingPluginWithHandle = HeadingPlugin.withComponent(HeadingWithHandle as any);
const ParagraphPluginWithHandle = ParagraphPlugin.withComponent(ParagraphWithHandle as any);
const PlecaPluginWithHandle = PlecaPlugin.withComponent(wrapWithHandle(PlecaBlock as any) as any);
const NotaPluginWithHandle = NotaPlugin.withComponent(wrapWithHandle(NotaBlock as any) as any);

// Slate-react's onDrop merges the dragged block's text into whatever
// contentEditable receives the drop. Calling preventDefault is not enough:
// Plate's onDrop pipeline (`pipeHandler` in @platejs/core) only falls through
// to slate-react's defaultPrevented check when every plugin handler returns
// null/undefined. DndPlugin's onDrop returns `getOptions().isDragging`, a
// boolean, which makes the pipeline return that boolean and bypass the
// defaultPrevented check entirely. Claim the drop here so slate-react treats
// it as handled and never touches the target's text. react-dnd performs the
// block reorder through its own registry, so blocking slate's onDrop does
// not affect dragging blocks to a new position.
const BlockSlateDropPlugin = createPlatePlugin({
  key: "cortes-block-slate-drop",
  handlers: { onDrop: () => true },
});

// Map a card block type back to its SelectableItem type so a dropped item and
// an already-present block hash to the same dedup key.
const CARD_TYPE_TO_ITEM: Record<string, string> = {
  [BLOCK_LOG]: "log",
  [BLOCK_OTF]: "otf",
  [BLOCK_TRANSCRIPT]: "transcript",
};

// Mirror SelectionContext.addItemsToProject's `assetKey` so dropping a card
// that's already in the corte is rejected the same way the library path does.
// Works on both raw SelectableItems (type "log"/"otf"/"transcript") and on
// editor blocks (type BLOCK_LOG/...), which preserve `selectedText`/`title`
// verbatim — we read those raw fields (not the normalized Slate children) so
// an OTF/transcript re-drop still matches its existing block.
function cortesAssetKey(o: any): string {
  const type = CARD_TYPE_TO_ITEM[o?.type as string] ?? o?.type;
  if (type === "log" || type === "transcript" || type === "otf") {
    const text = (o?.selectedText || o?.title || "").slice(0, 80);
    return `${type}:${o?.sourceId || o?.storyId || ""}:${o?.tc || ""}:${o?.author || ""}:${text}`;
  }
  return `${type}:${o?.sourceId || o?.storyId || o?.id}`;
}

// Pressing Enter would otherwise split the current block, and the new node
// inherits the current type — duplicating a log/pleca/etc. Instead, always
// drop a fresh empty Nota below and move the cursor into it.
const BreakToNotaPlugin = createPlatePlugin({ key: "cortes-break-nota" }).overrideEditor(
  ({ editor, tf: { insertBreak } }) => ({
    transforms: {
      insertBreak() {
        const entry = editor.api.block();
        if (!entry) {
          insertBreak();
          return;
        }
        const [, path] = entry;
        editor.tf.insertNodes(
          { type: BLOCK_NOTA, children: [{ text: "" }] },
          { at: PathApi.next(path), select: true },
        );
      },
    },
  }),
);

const PLUGINS = [
  // Plate DnD identifies the dragged/dropped block by `element.id`, so every
  // node must carry a stable id. NodeIdPlugin assigns them on normalization.
  NodeIdPlugin,
  BreakToNotaPlugin,
  BlockSlateDropPlugin,
  DndPlugin.configure({ options: { enableScroller: true } }),
  ParagraphPluginWithHandle,
  HeadingPluginWithHandle,
  // Log/OTF/Transcript blocks are created by dragging cards in, not inserted.
  LogPluginWithHandle,
  OtfPluginWithHandle,
  TranscriptPluginWithHandle,
  ChecklistPluginWithHandle,
  PlecaPluginWithHandle,
  NotaPluginWithHandle,
];

function InsertToolbar() {
  const editor = useEditorRef();

  const insertAtEnd = (node: TElement) => {
    const path: Path = [editor.children.length];
    editor.tf.insertNodes(node, { at: path });
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        padding: "8px 12px",
        borderBottom: "1px solid var(--color-panel-border)",
        background: "rgba(0,0,0,0.15)",
      }}
    >
      <span style={{ color: "#666", fontSize: "0.7rem", alignSelf: "center" }}>Insertar:</span>
      <ToolbarBtn
        icon="bx-captions"
        label="Pleca"
        color="#f59e0b"
        onClick={() => insertAtEnd({ type: BLOCK_PLECA, children: [{ text: "" }] })}
      />
      <ToolbarBtn
        icon="bx-note"
        label="Recurso"
        color="#94a3b8"
        onClick={() => insertAtEnd({ type: BLOCK_NOTA, children: [{ text: "" }] })}
      />
    </div>
  );
}

function ToolbarBtn({
  icon,
  label,
  color,
  onClick,
}: {
  icon: string;
  label: string;
  color?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 8px",
        borderRadius: 6,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        color: color ?? "#aaa",
        fontSize: "0.75rem",
        cursor: "pointer",
      }}
    >
      <i className={`bx ${icon}`} />
      {label}
    </button>
  );
}

function EditorDropZone({
  children,
  participantList,
}: {
  children: React.ReactNode;
  participantList?: any[];
}) {
  const editor = useEditorRef();
  const manager = useDragDropManager();
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  // dropLineY: Y position (px, relative to the container) where the indicator line should appear.
  // null = not dragging externally.
  const [dropLineY, setDropLineY] = useState<number | null>(null);
  // Read the latest participant list from a ref so the drop listeners
  // (registered once via useEffect) always see the current list without
  // re-attaching on every render.
  const participantListRef = useRef(participantList);
  participantListRef.current = participantList;

  // Helper: given drop coordinates, find the top-level Slate block under the cursor
  // and return { domIdx, insertBefore (true = top half) }.
  const computeInsertInfo = (clientX: number, clientY: number) => {
    const domEl = ref.current;
    if (!domEl) return null;
    const target = document.elementFromPoint(clientX, clientY);
    if (!target) return null;

    let node: Element | null = target;
    while (node && node !== domEl) {
      if (
        node.getAttribute("data-slate-node") === "element" &&
        node.parentElement === domEl.querySelector("[data-slate-editor]")
      ) {
        break;
      }
      if (node.parentElement && node.parentElement.hasAttribute("data-slate-editor")) {
        break;
      }
      node = node.parentElement;
    }

    if (!node || node === domEl) return null;

    const slateEditor = domEl.querySelector("[data-slate-editor]");
    if (!slateEditor) return null;

    const blockNodes = Array.from(slateEditor.children);
    const domIdx = blockNodes.indexOf(node as HTMLElement);
    if (domIdx < 0) return null;

    const rect = node.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const insertBefore = clientY < midY;
    return { domIdx, insertBefore, rect };
  };

  // External native drags (Explore cards) vs. react-dnd's internal block reorder
  // can't be told apart by MIME — react-dnd's HTML5Backend also sets
  // `application/json` on its own drags. The reliable discriminator is its
  // monitor: it's only "dragging" for an internal react-dnd source.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const monitor = manager.getMonitor();
    const isExternal = () => !monitor.isDragging();

    const insertFromDrop = (dt: DataTransfer, clientX: number, clientY: number) => {
      const multi = dt.getData("application/x-braindog-multi");
      const single = dt.getData("application/json");
      const items: SelectableItem[] = multi
        ? JSON.parse(multi)
        : single
          ? [JSON.parse(single)]
          : [];

      // Reject cards already present in the corte. Keys come from the current
      // card blocks plus any earlier item in this same drop (a multi-drag can
      // carry the same card twice).
      const seenKeys = new Set(
        (editor.children as TElement[])
          .filter((n) => CARD_TYPE_TO_ITEM[(n as any).type as string])
          .map((n) => cortesAssetKey(n)),
      );
      const freshItems = items.filter((it) => {
        // Only card-type items are deduped; non-card items don't become blocks
        // and are dropped by itemToBlock below, so never flag them as skipped.
        if (!(it.type === "log" || it.type === "otf" || it.type === "transcript")) return true;
        const key = cortesAssetKey(it);
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });
      const skipped = items.length - freshItems.length;

      const nodes = freshItems
        .map((it) => itemToBlock(it, participantListRef.current))
        .filter((n): n is TElement => !!n);
      if (nodes.length === 0) {
        if (skipped > 0) {
          toast.info(
            skipped === 1
              ? "Esa tarjeta ya está en el corte"
              : `${skipped} tarjetas ya estaban en el corte`,
          );
        }
        return;
      }

      // Determine insert position from drop coordinates.
      // Walk up from the element under the cursor to find a Slate block node.
      let insertAt: Path = [editor.children.length]; // fallback: append
      const domEl = ref.current;
      if (domEl) {
        const target = document.elementFromPoint(clientX, clientY);
        if (target) {
          // Find the nearest ancestor that is a top-level Slate element node
          let node: Element | null = target;
          while (node && node !== domEl) {
            if (
              node.getAttribute("data-slate-node") === "element" &&
              node.parentElement === domEl.querySelector("[data-slate-editor]")
            ) {
              break;
            }
            // Also match children of the slate editor directly
            if (node.parentElement && node.parentElement.hasAttribute("data-slate-editor")) {
              break;
            }
            node = node.parentElement;
          }

          if (node && node !== domEl) {
            // Find which index this DOM node maps to in editor.children
            const slateEditor = domEl.querySelector("[data-slate-editor]");
            if (slateEditor) {
              const blockNodes = Array.from(slateEditor.children);
              const domIdx = blockNodes.indexOf(node as HTMLElement);
              if (domIdx >= 0) {
                const rect = node.getBoundingClientRect();
                const midY = rect.top + rect.height / 2;
                // Insert before the hovered block if dropping on the top half, after if bottom half
                insertAt = clientY < midY ? [domIdx] : [domIdx + 1];
              }
            }
          }
        }
      }

      editor.tf.insertNodes(nodes, { at: insertAt });

      if (skipped > 0) {
        toast.info(
          `${nodes.length} agregada${nodes.length === 1 ? "" : "s"} · ${skipped} ya estaba${skipped === 1 ? "" : "n"} en el corte`,
        );
      }
    };

    // Capture phase: intercept the external drag before it reaches Slate's
    // editable AND stop it from bubbling to react-dnd's window listener, which
    // would otherwise force dropEffect='none' and cancel the drop entirely.
    //
    // For INTERNAL Plate dnd drags (block reorder) the text-merge into the
    // target is suppressed by BlockSlateDropPlugin (slate-react's onDrop is
    // claimed by Plate so it never runs). react-dnd's HTML5 backend performs
    // the reorder through its own registry, independent of slate's onDrop.
    // We do NOT stopPropagation for internal drags so react-dnd's listeners
    // still see the event.
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      if (isExternal()) {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
        setHover(true);

        // Compute drop line Y position relative to the container
        const domEl = ref.current;
        if (domEl) {
          const containerRect = domEl.getBoundingClientRect();
          const target = document.elementFromPoint(e.clientX, e.clientY);
          if (target) {
            let node: Element | null = target;
            while (node && node !== domEl) {
              if (node.parentElement && node.parentElement.hasAttribute("data-slate-editor")) break;
              if (
                node.getAttribute("data-slate-node") === "element" &&
                node.parentElement === domEl.querySelector("[data-slate-editor]")
              )
                break;
              node = node.parentElement;
            }
            if (node && node !== domEl) {
              const slateEditor = domEl.querySelector("[data-slate-editor]");
              if (slateEditor) {
                const blockNodes = Array.from(slateEditor.children);
                const domIdx = blockNodes.indexOf(node as HTMLElement);
                if (domIdx >= 0) {
                  const rect = node.getBoundingClientRect();
                  const midY = rect.top + rect.height / 2;
                  const lineY =
                    e.clientY < midY
                      ? rect.top - containerRect.top + domEl.scrollTop
                      : rect.bottom - containerRect.top + domEl.scrollTop;
                  setDropLineY(lineY);
                  return;
                }
              }
            }
          }
          // No block found — show line at bottom
          const slateEditor = domEl.querySelector("[data-slate-editor]");
          if (slateEditor) {
            const r = slateEditor.getBoundingClientRect();
            setDropLineY(r.bottom - containerRect.top + domEl.scrollTop);
          } else {
            setDropLineY(null);
          }
        }
      } else {
        e.preventDefault();
      }
    };
    const onDragLeave = (e: DragEvent) => {
      if (!isExternal()) return;
      const related = e.relatedTarget as Node | null;
      if (!related || !el.contains(related)) {
        setHover(false);
        setDropLineY(null);
      }
    };
    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      if (!isExternal()) {
        // Block Slate from merging text into the target; let react-dnd reorder.
        e.preventDefault();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setHover(false);
      setDropLineY(null);
      try {
        insertFromDrop(e.dataTransfer, e.clientX, e.clientY);
      } catch (err) {
        console.error("[CortesEditor] drop error", err);
      }
    };

    el.addEventListener("dragover", onDragOver, true);
    el.addEventListener("dragleave", onDragLeave, true);
    el.addEventListener("drop", onDrop, true);
    return () => {
      el.removeEventListener("dragover", onDragOver, true);
      el.removeEventListener("dragleave", onDragLeave, true);
      el.removeEventListener("drop", onDrop, true);
    };
  }, [manager, editor]);

  return (
    <div
      ref={ref}
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "12px 16px",
        background: hover ? "rgba(16, 185, 129, 0.07)" : "transparent",
        border: hover ? "2px dashed var(--color-role-sp)" : "2px dashed transparent",
        borderRadius: 8,
        transition: "background 0.15s ease, border-color 0.15s ease",
        position: "relative",
      }}
    >
      {/* Drop position indicator line */}
      {hover && dropLineY !== null && (
        <div
          style={{
            position: "absolute",
            left: 12,
            right: 12,
            top: dropLineY,
            height: 3,
            borderRadius: 2,
            background: "var(--color-role-sp, #10b981)",
            boxShadow:
              "0 0 8px var(--color-role-sp, #10b981), 0 0 20px var(--color-role-sp, #10b981)",
            pointerEvents: "none",
            zIndex: 100,
            transform: "translateY(-50%)",
          }}
        />
      )}
      {children}
    </div>
  );
}

/**
 * Fixed VTR header — título and sinopsis live OUTSIDE the Plate document so
 * they can never be reordered or dragged. Fecha is the project's creation date;
 * autor is the project owner if known, otherwise the logged-in user. The
 * "Guardar" button materializes the current Plate doc into `assembly_items`
 * (canonical for summaries/exports) plus the `cortes_doc` JSONB snapshot.
 */
function VtrHeader({
  project,
  titulo,
  setTitulo,
  sinopsis,
  setSinopsis,
  onSave,
  saveStatus,
  onExport,
  onExportPdf,
  onExportDoc,
  readOnly = false,
}: {
  project: AssemblyProject;
  titulo: string;
  setTitulo: (v: string) => void;
  sinopsis: string;
  setSinopsis: (v: string) => void;
  onSave: () => void;
  saveStatus: "idle" | "saving" | "saved" | "error";
  onExport: () => void;
  onExportPdf: () => void;
  onExportDoc: () => void;
  readOnly?: boolean;
}) {
  const { user } = useAuth();
  const { participantList } = useProjects();
  const fecha = useMemo(
    () => (project.createdAt ? new Date(project.createdAt) : new Date()),
    [project.createdAt],
  );
  const fechaStr = fecha.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const autor = resolveAutorName(project.createdBy, participantList, user?.name);

  return (
    <div
      style={{
        padding: "12px 16px",
        borderBottom: "1px solid var(--color-panel-border)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        background: "rgba(0,0,0,0.12)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          readOnly={readOnly}
          placeholder="Título del VTR"
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#fff",
            fontSize: "1.1rem",
            fontWeight: 800,
            letterSpacing: 0.3,
          }}
        />
        <button
          type="button"
          onClick={onExport}
          title="Exportar el corte como .txt"
          style={{
            padding: "6px 12px",
            borderRadius: 6,
            background: "rgba(255,255,255,0.06)",
            color: "#cbd5e1",
            border: "1px solid var(--color-panel-border)",
            fontSize: "0.78rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <i className="bx bx-file-blank" />
          TXT
        </button>
        {SHOW_PDF_EXPORT && (
          <button
            type="button"
            onClick={onExportPdf}
            title="Exportar el corte como .pdf"
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              background: "rgba(255,255,255,0.06)",
              color: "#cbd5e1",
              border: "1px solid var(--color-panel-border)",
              fontSize: "0.78rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i className="bx bxs-file-pdf" />
            PDF
          </button>
        )}
        <button
          type="button"
          onClick={onExportDoc}
          title="Exportar el corte como .doc"
          style={{
            padding: "6px 12px",
            borderRadius: 6,
            background: "rgba(255,255,255,0.06)",
            color: "#cbd5e1",
            border: "1px solid var(--color-panel-border)",
            fontSize: "0.78rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <i className="bx bxs-file-doc" />
          DOC
        </button>
        {!readOnly && (
          <button
            type="button"
            onClick={onSave}
            disabled={saveStatus === "saving"}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              background: "var(--color-role-sp, #22c55e)",
              color: "#fff",
              border: "none",
              fontSize: "0.78rem",
              fontWeight: 600,
              cursor: saveStatus === "saving" ? "wait" : "pointer",
              opacity: saveStatus === "saving" ? 0.7 : 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i className="bx bx-save" />
            {saveStatus === "saving"
              ? "Guardando…"
              : saveStatus === "saved"
                ? "Guardado"
                : saveStatus === "error"
                  ? "Reintentar"
                  : "Guardar"}
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 16, fontSize: "0.72rem", color: "#888" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <i className="bx bx-calendar" />
          {fechaStr}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <i className="bx bx-user" />
          {autor}
        </span>
      </div>
      <textarea
        value={sinopsis}
        onChange={(e) => setSinopsis(e.target.value)}
        readOnly={readOnly}
        placeholder="Sinopsis…"
        rows={2}
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid var(--color-panel-border)",
          borderRadius: 6,
          outline: "none",
          color: "#cbd5e1",
          fontSize: "0.82rem",
          lineHeight: 1.5,
          padding: "6px 8px",
          resize: "vertical",
          fontFamily: "inherit",
        }}
      />
    </div>
  );
}

export default function CortesEditor({
  project: projectProp,
  onUpdateProject,
  onItemsChange,
  readOnly = false,
  onTimecodeClick,
  activeMockTc,
}: {
  project?: AssemblyProject;
  onUpdateProject?: (id: string, updates: Partial<AssemblyProject>) => void;
  onItemsChange?: (items: SelectableItem[]) => void;
  readOnly?: boolean;
  onTimecodeClick?: (item: any) => void;
  activeMockTc?: string;
} = {}) {
  const { getActiveProject, setNavigateToSource } = useSelection();
  // When invoked from the VTR accordion the caller passes the approved project
  // directly (it lives in AssemblyManager's local state, not in the selection
  // context). Otherwise we fall back to the active project from context — the
  // CUT panel's existing behavior.
  const project = projectProp ?? getActiveProject();

  const onNavigateSource = (sourceId: string, itemId: string, itemType: string) => {
    setNavigateToSource({
      storyId: sourceId,
      itemId,
      itemType: itemType as "log" | "transcript",
    });
  };

  if (!project) {
    return (
      <div
        data-testid="cortes-editor-empty"
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#666",
          fontSize: "0.85rem",
          padding: 24,
          textAlign: "center",
          background: "var(--color-panel-bg)",
        }}
      >
        Seleccioná o creá un proyecto para empezar a editar cortes.
      </div>
    );
  }

  // The key forces a remount when switching projects so the underlying Plate
  // editor instance — initialized once with `value` — is recreated with the
  // new project's seed instead of retaining the previous project's document.
  return (
    <CortesEditorForProject
      key={project.id}
      project={project}
      onUpdateProject={onUpdateProject}
      onItemsChange={onItemsChange}
      readOnly={readOnly}
      onNavigateSource={onNavigateSource}
      onTimecodeClick={onTimecodeClick}
      activeMockTc={activeMockTc}
    />
  );
}

function CortesEditorForProject({
  project,
  onUpdateProject,
  onItemsChange,
  readOnly = false,
  onNavigateSource,
  onTimecodeClick,
  activeMockTc,
}: {
  project: AssemblyProject;
  onUpdateProject?: (id: string, updates: Partial<AssemblyProject>) => void;
  onItemsChange?: (items: SelectableItem[]) => void;
  readOnly?: boolean;
  onNavigateSource?: (sourceId: string, itemId: string, itemType: string) => void;
  onTimecodeClick?: (item: any) => void;
  activeMockTc?: string;
}) {
  const { updateProject: ctxUpdateProject } = useSelection();
  // Route saves through the caller-provided handler when available so the
  // VTR view can also patch its local `approvedProjects` cache. CUT view
  // omits the prop and uses the selection context directly.
  const updateProject = onUpdateProject ?? ctxUpdateProject;
  const { user } = useAuth();
  const { participantList } = useProjects();
  const { tags: allTags, categories, getTagLabel, getCameraName } = useTags();
  const [titulo, setTitulo] = useState(project.name ?? "");
  const [sinopsis, setSinopsis] = useState(project.vtrDescription ?? "");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolver de los marcadores Stream/Ubicación que pinta el header de cada
  // bloque en el export. Combina dos fuentes que el logger persiste por
  // separado:
  //   1. log.cameras[] — los chips de la sección STREAM del logger
  //      (tabla `cameras`, ej. "STREAM 1", "STREAM 2").
  //   2. log.tags[] filtrado por categoría de ubicación, usando la misma
  //      regex que LogCard.isStreamOrLocationTag (sincronizalas si tocás
  //      cualquiera de las dos).
  // Acepta el node entero porque ambas listas viven en campos distintos.
  const resolveStreamTags = useMemo(() => {
    return (node: any): string[] => {
      const out: string[] = [];

      const cameraIds = (node?.cameras as string[] | undefined) || [];
      for (const id of cameraIds) {
        const name = getCameraName(id);
        if (name) out.push(name);
      }

      const tagIds = (node?.tags as string[] | undefined) || [];
      for (const id of tagIds) {
        const tag = allTags.find((t) => t.id === id);
        if (!tag) continue;
        const cat = categories.find((c) => c.id === tag.categoryId);
        const catName = (cat as any)?.name || "";
        if (
          !/stream|ubicaci|location|espacio|lugar|foro|canal|c[áa]m(?:ara|era)|\bset\b/i.test(
            catName,
          )
        )
          continue;
        const label = getTagLabel(id) || tag.label;
        if (label) out.push(label);
      }

      return out;
    };
  }, [allTags, categories, getTagLabel, getCameraName]);

  const initialValue = useMemo(() => seedFromProject(project), [project.id]);

  // Scope react-dnd's HTML5Backend to THIS container instead of `window`.
  // By default the backend listens on window and its `handleTopDragStart`
  // calls preventDefault on any native drag that isn't a react-dnd source —
  // which kills the native drag of the LIBRARY cards page-wide whenever the
  // editor is mounted. Binding `rootElement` to the editor confines react-dnd
  // to internal block reordering and leaves outside native drags untouched.
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const dndOptions = useMemo(() => ({ rootElement: rootEl }), [rootEl]);

  const editor = usePlateEditor({
    plugins: PLUGINS,
    value: initialValue,
  });

  // Deriva los items del doc EN VIVO y los emite al padre (debounced) para que el
  // player de cortes refleje lo que se edita sin necesidad de "Guardar". Los ids
  // de bloque son estables (NodeIdPlugin), así que los itemId se conservan entre
  // emisiones y el playout no pierde el segmento activo.
  const onItemsChangeRef = useRef(onItemsChange);
  onItemsChangeRef.current = onItemsChange;
  const emitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Firma de lo que el player necesita de cada item (orden, ubicación temporal y
  // canal). Editar el texto de un bloque no la cambia → no re-emitimos y evitamos
  // recomputar segmentos / recargar el player mientras se escribe.
  const lastSigRef = useRef<string | null>(null);
  const emitLiveItems = () => {
    if (!onItemsChangeRef.current) return;
    try {
      const items = docToItems(editor.children as TElement[]);
      const sig = JSON.stringify(
        items.map((it) => [
          it.id,
          it.type,
          it.tcIn,
          it.tcInMs,
          it.tcOut,
          it.tcOutMs,
          it.tc,
          it.cameras,
          it.tags,
        ]),
      );
      if (sig === lastSigRef.current) return;
      lastSigRef.current = sig;
      onItemsChangeRef.current(items);
    } catch (e) {
      console.error("[CortesEditor] live items emit failed", e);
    }
  };
  const scheduleEmitLiveItems = () => {
    if (emitTimerRef.current) clearTimeout(emitTimerRef.current);
    emitTimerRef.current = setTimeout(emitLiveItems, 250);
  };

  // Emisión inicial: el player ya refleja el doc sembrado antes de cualquier edición.
  useEffect(() => {
    emitLiveItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      if (blurSaveTimerRef.current) clearTimeout(blurSaveTimerRef.current);
      if (emitTimerRef.current) clearTimeout(emitTimerRef.current);
    },
    [],
  );

  // Un BTR no se puede exportar sin al menos una pleca: es la que separa y
  // titula los bloques del corte. Sin pleca el export no tiene estructura.
  const hasPleca = (doc: TElement[]) => doc.some((n) => (n as any).type === BLOCK_PLECA);

  const handleExport = () => {
    const doc = editor.children as TElement[];
    if (!hasPleca(doc)) {
      toast.warning("Agregá al menos una pleca antes de exportar");
      return;
    }
    try {
      const autor = resolveAutorName(project.createdBy, participantList, user?.name);
      const txt = buildExportTxt(project, titulo, sinopsis, doc, autor, resolveStreamTags);
      const filename = `${sanitizeFilename(titulo || project.name || "corte")}.txt`;
      downloadTxt(filename, txt);
    } catch (e) {
      console.error("[CortesEditor] export failed", e);
    }
  };

  const handleExportPdf = () => {
    if (!sinopsis?.trim()) {
      toast.error("Agregá una sinopsis antes de exportar");
      return;
    }
    const doc = editor.children as TElement[];
    if (!hasPleca(doc)) {
      toast.warning("Agregá al menos una pleca antes de exportar");
      return;
    }
    try {
      const autor = resolveAutorName(project.createdBy, participantList, user?.name);
      const pdf = buildExportPdf(project, titulo, sinopsis, doc, autor, resolveStreamTags);
      const filename = `${sanitizeFilename(titulo || project.name || "corte")}.pdf`;
      downloadPdf(filename, pdf);
    } catch (e) {
      console.error("[CortesEditor] PDF export failed", e);
    }
  };

  const handleExportDoc = async () => {
    if (!sinopsis?.trim()) {
      toast.error("Agregá una sinopsis antes de exportar");
      return;
    }
    const doc = editor.children as TElement[];
    if (!hasPleca(doc)) {
      toast.warning("Agregá al menos una pleca antes de exportar");
      return;
    }
    try {
      const autor = resolveAutorName(project.createdBy, participantList, user?.name);
      const docFile = buildExportDoc(project, titulo, sinopsis, doc, autor, resolveStreamTags);
      const filename = `${sanitizeFilename(titulo || project.name || "corte")}.docx`;
      await downloadDoc(filename, docFile);
    } catch (e) {
      console.error("[CortesEditor] DOC export failed", e);
    }
  };

  const handleSave = () => {
    if (readOnly) return;
    setSaveStatus("saving");
    try {
      const doc = editor.children as TElement[];
      updateProject(project.id, {
        name: titulo,
        vtrDescription: sinopsis,
        items: docToItems(doc),
        cortesDoc: doc,
      });
      setSaveStatus("saved");
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 1800);
    } catch (e) {
      console.error("[CortesEditor] save failed", e);
      setSaveStatus("error");
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleAutoSave = () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSaveRef.current();
    }, 500);
  };

  // Autoguardar cambios cuando el usuario modifique el título o la sinopsis
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    scheduleAutoSave();
  }, [titulo, sinopsis]);

  // Auto-save when focus genuinely leaves the editor. React's onBlur on the
  // container bubbles focusout, so blurs that hop between inner elements
  // keep `relatedTarget` inside the root and are ignored. We also ignore
  // focus moving into Radix portals (Dialog/Popover/Menu/Listbox) — those
  // are editor-owned UI rendered outside the DOM tree, not "leaving".
  // The 150ms debounce + activeElement re-check covers Radix's transient
  // null-focus before the portal receives focus, and lets a focusin cancel
  // a pending save if the user is just bouncing back into the editor.
  const PORTAL_SELECTOR =
    '[role="dialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]';

  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const next = e.relatedTarget as Element | null;
    if (next && e.currentTarget.contains(next)) return;
    if (next && next.closest(PORTAL_SELECTOR)) return;
    if (blurSaveTimerRef.current) clearTimeout(blurSaveTimerRef.current);
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    blurSaveTimerRef.current = setTimeout(() => {
      blurSaveTimerRef.current = null;
      const active = document.activeElement as Element | null;
      if (active && active.closest(PORTAL_SELECTOR)) return;
      handleSaveRef.current();
    }, 150);
  };

  const handleContainerFocus = () => {
    if (blurSaveTimerRef.current) {
      clearTimeout(blurSaveTimerRef.current);
      blurSaveTimerRef.current = null;
    }
  };

  const lastTimecodeRequestRef = useRef<{ key: string; requestedAt: number } | null>(null);
  const requestTimecodePlayback = useCallback(
    (item: any) => {
      if (!onTimecodeClick) return;

      // Selecting a Plate block emits onChange before the card's click handler
      // runs. Both routes intentionally support playback (keyboard navigation
      // and pointer clicks), so collapse the pair into one request.
      const key = [
        item?.id,
        item?.sourceId,
        item?.startAt ?? item?.start_at ?? item?.createdAt ?? item?.created_at,
        item?.tcIn,
        item?.tc,
      ]
        .filter((value) => value != null && value !== "")
        .join("|");
      const requestedAt = Date.now();
      const previous = lastTimecodeRequestRef.current;
      if (key && previous?.key === key && requestedAt - previous.requestedAt < 300) return;

      lastTimecodeRequestRef.current = { key, requestedAt };
      onTimecodeClick(item);
    },
    [onTimecodeClick],
  );

  const ctxValue = useMemo(
    () => ({
      onNavigateSource,
      onTimecodeClick: requestTimecodePlayback,
      activeMockTc,
    }),
    [requestTimecodePlayback, activeMockTc], // onNavigateSource is stable but recreating is fine
  );

  const lastSelectedNodeIdRef = useRef<string | null>(null);

  useEffect(() => {
    lastSelectedNodeIdRef.current = null;
  }, [activeMockTc]);

  return (
    <CortesEditorContext.Provider value={ctxValue}>
      <div
        ref={setRootEl}
        data-testid="cortes-editor"
        onBlur={handleContainerBlur}
        onFocus={handleContainerFocus}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "var(--color-panel-bg)",
        }}
      >
        {rootEl && (
          <DndProvider backend={HTML5Backend} options={dndOptions}>
            <Plate
              editor={editor}
              readOnly={readOnly}
              onChange={() => {
                if (!readOnly) {
                  scheduleEmitLiveItems();
                  scheduleAutoSave();
                }

                // Detectar bloque seleccionado (para clicks y flechas teclado)
                try {
                  const entry = editor.api.block();
                  if (entry) {
                    const [node] = entry;
                    if (
                      node.type === BLOCK_LOG ||
                      node.type === BLOCK_OTF ||
                      node.type === BLOCK_TRANSCRIPT
                    ) {
                      const nodeId = (node as any).id;
                      if (nodeId && nodeId !== lastSelectedNodeIdRef.current) {
                        lastSelectedNodeIdRef.current = nodeId;
                        requestTimecodePlayback(node);
                      }
                    }
                  }
                } catch (e) {
                  // Evitar fallos de renderizado si el editor está desmontando
                }
              }}
            >
              <VtrHeader
                project={project}
                titulo={titulo}
                setTitulo={setTitulo}
                sinopsis={sinopsis}
                setSinopsis={setSinopsis}
                onSave={handleSave}
                saveStatus={saveStatus}
                onExport={handleExport}
                onExportPdf={handleExportPdf}
                onExportDoc={handleExportDoc}
                readOnly={readOnly}
              />
              {!readOnly && <InsertToolbar />}
              <EditorDropZone participantList={participantList}>
                <PlateContent
                  readOnly={readOnly}
                  style={{
                    outline: "none",
                    color: "#ddd",
                    fontSize: "0.85rem",
                    lineHeight: 1.55,
                    minHeight: "100%",
                  }}
                  placeholder="Arrastrá un log, OTF o transcripción desde Explore…"
                />
              </EditorDropZone>
            </Plate>
          </DndProvider>
        )}
      </div>
    </CortesEditorContext.Provider>
  );
}
